import {
  useMemo,
  useState,
} from "react";

import {
  useAuth,
} from "../auth/AuthContext";

import type {
  Page,
} from "../App";

type MobileNavProps = {
  page: Page;

  onNavigate: (
    page: Page,
  ) => void;
};

type Permission =
  | "dashboard.view"
  | "comandas.view"
  | "quadras.view"
  | "turmas.view"
  | "clientes.view"
  | "estoque.view"
  | "financeiro.view"
  | "funcionarios.view";

type NavItem = {
  page: Page;
  label: string;
  icon: string;
  permission: Permission;
};

const PRIMARY_ITEMS: NavItem[] = [
  {
    page: "dashboard",
    label: "Início",
    icon: "⌂",
    permission:
      "dashboard.view",
  },
  {
    page: "comandas",
    label: "Comandas",
    icon: "◫",
    permission:
      "comandas.view",
  },
  {
    page: "quadras",
    label: "Quadras",
    icon: "◇",
    permission:
      "quadras.view",
  },
  {
    page: "turmas",
    label: "Aulas",
    icon: "▦",
    permission:
      "turmas.view",
  },
];

const MORE_ITEMS: NavItem[] = [
  {
    page: "clientes",
    label: "Clientes",
    icon: "◎",
    permission:
      "clientes.view",
  },
  {
    page: "estoque",
    label: "Estoque",
    icon: "□",
    permission:
      "estoque.view",
  },
  {
    page: "financeiro",
    label: "Financeiro",
    icon: "$",
    permission:
      "financeiro.view",
  },
  {
    page: "funcionarios",
    label: "Funcionários",
    icon: "♙",
    permission:
      "funcionarios.view",
  },
];

export default function MobileNav({
  page,
  onNavigate,
}: MobileNavProps) {
  const {
    can,
  } = useAuth();

  const [
    moreOpen,
    setMoreOpen,
  ] =
    useState(false);

  const primaryItems =
    useMemo(
      () =>
        PRIMARY_ITEMS.filter(
          (item) =>
            can(
              item.permission,
            ),
        ),
      [
        can,
      ],
    );

  const moreItems =
    useMemo(
      () =>
        MORE_ITEMS.filter(
          (item) =>
            can(
              item.permission,
            ),
        ),
      [
        can,
      ],
    );

  const moreActive =
    moreItems.some(
      (item) =>
        item.page ===
        page,
    );

  function navigate(
    target: Page,
  ) {
    setMoreOpen(
      false,
    );

    onNavigate(
      target,
    );
  }

  return (
    <>
      {moreOpen &&
        moreItems.length >
          0 && (
          <div
            className="mobile-more-backdrop"
            onClick={() =>
              setMoreOpen(
                false,
              )
            }
          >
            <div
              className="mobile-more-sheet"
              onClick={(
                event,
              ) =>
                event.stopPropagation()
              }
              role="dialog"
              aria-modal="true"
              aria-label="Mais opções"
            >
              <div className="mobile-more-handle" />

              <div className="mobile-more-header">
                <div>
                  <span className="mobile-more-eyebrow">
                    SUNSET ONE
                  </span>

                  <strong>
                    Mais opções
                  </strong>
                </div>

                <button
                  type="button"
                  className="mobile-more-close"
                  onClick={() =>
                    setMoreOpen(
                      false,
                    )
                  }
                  aria-label="Fechar menu"
                >
                  ×
                </button>
              </div>

              <div className="mobile-more-grid">
                {moreItems.map(
                  (item) => (
                    <button
                      key={
                        item.page
                      }
                      type="button"
                      className={
                        page ===
                        item.page
                          ? "mobile-more-item active"
                          : "mobile-more-item"
                      }
                      onClick={() =>
                        navigate(
                          item.page,
                        )
                      }
                    >
                      <span className="mobile-more-icon">
                        {
                          item.icon
                        }
                      </span>

                      <span className="mobile-more-label">
                        {
                          item.label
                        }
                      </span>

                      <span className="mobile-more-arrow">
                        ›
                      </span>
                    </button>
                  ),
                )}
              </div>
            </div>
          </div>
        )}

      <nav
        className="mobile-navigation"
        aria-label="Navegação mobile"
      >
        {primaryItems.map(
          (item) => (
            <button
              key={
                item.page
              }
              type="button"
              className={
                page ===
                item.page
                  ? "mobile-navigation-button active"
                  : "mobile-navigation-button"
              }
              onClick={() =>
                navigate(
                  item.page,
                )
              }
            >
              <span className="mobile-navigation-icon">
                {
                  item.icon
                }
              </span>

              <span>
                {
                  item.label
                }
              </span>
            </button>
          ),
        )}

        {moreItems.length >
          0 && (
          <button
            type="button"
            className={
              moreActive ||
              moreOpen
                ? "mobile-navigation-button active"
                : "mobile-navigation-button"
            }
            onClick={() =>
              setMoreOpen(
                (current) =>
                  !current,
              )
            }
            aria-expanded={
              moreOpen
            }
            aria-label="Mais opções"
          >
            <span className="mobile-navigation-icon">
              •••
            </span>

            <span>
              Mais
            </span>
          </button>
        )}
      </nav>
    </>
  );
}